import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Award, Video, Loader2 } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { BookOpen, Award, Video };

const Academy = () => {
  useDocumentMeta({
    title: 'Academy | Revs Vibes',
    description: 'Learn about collector cars through the Revs Vibes Academy featuring courses, workshops, and automotive education.',
    canonical: 'https://www.revsvibes.com/academy',
  });
  const { language } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/academy`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setCourses(data))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  const t = {
    en: { title: 'ACADEMY', subtitle: 'Learn from industry experts about car collecting and investment', enroll: 'Enroll Now', duration: 'Duration:', level: 'Level:' },
    es: { title: 'ACADEMIA', subtitle: 'Aprende de expertos de la industria sobre colección e inversión de autos', enroll: 'Inscribirse', duration: 'Duración:', level: 'Nivel:' },
    pt: { title: 'ACADEMIA', subtitle: 'Aprenda com especialistas sobre coleção e investimento em carros', enroll: 'Inscrever-se', duration: 'Duração:', level: 'Nível:' },
    fr: { title: 'ACADÉMIE', subtitle: "Apprenez des experts de l'industrie sur la collection et l'investissement automobile", enroll: "S'inscrire", duration: 'Durée:', level: 'Niveau:' },
    it: { title: 'ACCADEMIA', subtitle: "Impara dagli esperti del settore sulla collezione e l'investimento automobilistico", enroll: 'Iscriviti', duration: 'Durata:', level: 'Livello:' },
  };
  const tr = t[language] || t.en;

  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-96 bg-black text-white overflow-hidden clickable-card">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1600" alt="Academy" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="academy-title">{tr.title}</h1>
            <p className="text-xl text-gray-300">{tr.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={32} /></div>
          ) : courses.length === 0 ? (
            <p className="text-center text-gray-400 py-20">No courses available.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map(course => {
                const IconComp = ICON_MAP[course.icon] || BookOpen;
                return (
                  <Card key={course.id} className="clickable-card border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`course-card-${course.id}`}>
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-6 bg-black rounded-full flex items-center justify-center">
                        <IconComp className="text-white" size={28} />
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-black">{course.title}</h3>
                      {course.description && <p className="text-gray-600 text-sm mb-4 line-clamp-3">{course.description}</p>}
                      <div className="text-sm text-gray-600 space-y-1 mb-6">
                        <p>{tr.duration} {course.duration}</p>
                        <p>{tr.level} {course.level}</p>
                      </div>
                      <Button className="bg-black hover:bg-gray-800 text-white w-full" data-testid={`course-enroll-${course.id}`}>{tr.enroll}</Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Academy;
